import React, {useEffect, useState} from "react";
import {T, useTranslate} from "@tolgee/react";
import FfairModal from "view/components/modal/FfairModal";
import {useVersion} from "store/version/version.store";
import {
  Box,
  Button as MuiButton,
  CircularProgress,
  Typography,
} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import {useSnackbar} from "notistack";
import {Refresh} from "@material-ui/icons";
import useStyles from "./styles";

const VersionModal: React.FC = () => {
  const classes = useStyles();
  const {t} = useTranslate();

  const [isShowFullScreenLoader, setIsShowFullScreenLoader] = useState(false);

  const {enqueueSnackbar} = useSnackbar();

  const {autoCatch, fetch, checkBuildVersion} = useVersion();

  const isOpenModal = useVersion((state) => state.isOpenModal);
  const lastSnackbarUpdate = useVersion((state) => state.lastSnackbarUpdate);

  useEffect(() => {
    const bootstrap = async () => {
      await autoCatch();
      // A freshly loaded page already runs the newest bundle, so a version
      // raised while this browser was away needs no refresh prompt.
      await Promise.all([fetch({silent: true}), checkBuildVersion()]);
    };

    // AppTolgeeProvider mounts the app only after its network bootstrap, by
    // which point `load` has usually fired and would never reach a listener
    // added now.
    if (document.readyState === "complete") {
      bootstrap();
      return undefined;
    }

    window.addEventListener("load", bootstrap, {once: true});
    return () => window.removeEventListener("load", bootstrap);
  }, [autoCatch, fetch, checkBuildVersion]);

  const refreshPage = () => {
    setIsShowFullScreenLoader(true);
    // The host serves index.html and version.json with no-store, so a plain
    // reload pulls the new entry document, which references the new hashed
    // chunks. No need to clear localStorage/sessionStorage — that would log
    // the user out and drop persisted UI state.
    window.location.reload();
  };

  useEffect(() => {
    if (!lastSnackbarUpdate) {
      return;
    }

    enqueueSnackbar(t("global.version.upgraded_snackbar"), {
      variant: "default",
      persist: true,
      preventDuplicate: true,
      action: () => (
        <MuiButton
          size="small"
          variant="text"
          color="primary"
          onClick={refreshPage}
        >
          <T keyName="global.version.refresh_page" />
        </MuiButton>
      ),
    });
  }, [lastSnackbarUpdate]);

  if (isShowFullScreenLoader) {
    return (
      <div className={classes.loaderContainer}>
        <CircularProgress size={24} />
      </div>
    );
  }

  return (
    <FfairModal open={isOpenModal}>
      <FfairModal.Body className={classes.modalRoot}>
        <Box className={classes.paper}>
          <Typography variant="h6">
            <T keyName="global.version.title" />
          </Typography>
          <Typography variant="h6" className={classes.text}>
            <T keyName="global.version.subtitle" />
          </Typography>
          <div className={classes.buttonContainer}>
            <Button
              variant="contained"
              type="submit"
              color="primary"
              onClick={refreshPage}
              startIcon={<Refresh />}
            >
              <T keyName="global.version.refresh_page" />
            </Button>
          </div>
        </Box>
      </FfairModal.Body>
    </FfairModal>
  );
};

export default VersionModal;
